import React, { useState } from 'react';
import { Card, CardBody, Table, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { PaymentTransaction } from '../api/superAdminPayments/paymentsService';
import { Transaction } from '../pages/PaymentTransactions/Transactions/types';
import { downloadUnifiedInvoice } from '../utils/unifiedInvoiceService';
import SharedDownloadOverlay from './SharedDownloadOverlay';
import ShimmerRow from './ShimmerRow';
import './DashboardStyles.css';

// Universal transaction interface that can handle all data types
interface UniversalTransaction {
  _id: string;
  transactionId?: string;
  tournamentName: string;
  teamName: string;
  date: string;
  ageCategory?: string;
  amount: number;
  paymentMode: string;
  status: string;
  description?: string;
  // Original data for actions
  originalData?: PaymentTransaction | Transaction | any;
}

interface TransactionTableProps {
  transactions: PaymentTransaction[] | Transaction[] | UniversalTransaction[];
  viewAllLink?: string;
  title?: string;
  showActions?: boolean;
  showHeader?: boolean;
  maxRows?: number;
  loading?: boolean;
  onViewTransaction?: (transaction: any) => void;
  isDashboard?: boolean; // For dashboard vs full-page styling
}

const TransactionTable: React.FC<TransactionTableProps> = ({ 
  transactions, 
  viewAllLink,
  title = "Recent Transactions",
  showActions = false,
  showHeader = true,
  maxRows,
  loading = false,
  onViewTransaction,
  isDashboard = true
}) => {
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingTransactionId, setDownloadingTransactionId] = useState<string | null>(null);

  // Transform different data types to universal format
  const transformToUniversal = (transaction: any): UniversalTransaction => {
    // Handle PaymentTransaction type (SuperAdmin Payments & Dashboard)
    if (transaction.tournamentInfo && transaction.teamInfo) {
      return {
        _id: transaction._id,
        transactionId: transaction.paymentId || transaction._id.slice(-8).toUpperCase(),
        tournamentName: transaction.tournamentInfo.name,
        teamName: transaction.teamInfo.name,
        date: transaction.paidAt || transaction.createdAt,
        ageCategory: transaction.ageCategory || transaction.teamInfo.ageCategory || transaction.tournamentInfo.ageCategory || 'N/A',
        amount: transaction.amount,
        paymentMode: transaction.method,
        status: transaction.paidStatus,
        description: `${transaction.milestone} - ${transaction.type}`,
        originalData: transaction
      };
    }
    
    // Handle Transaction type (PaymentTransactions & Dashboard)
    if (transaction.transactionId && transaction.tournamentName) {
      return {
        _id: transaction._id,
        transactionId: transaction.paymentId || transaction.transactionId,
        tournamentName: transaction.tournamentName,
        teamName: transaction.teamName || transaction.team,
        date: transaction.date,
        ageCategory: transaction.ageCategory || transaction.teamInfo?.ageCategory || transaction.tournamentInfo?.ageCategory || 'N/A',
        amount: transaction.amount,
        paymentMode: transaction.paymentMode || transaction.paymentMethod,
        status: transaction.status,
        description: transaction.description,
        originalData: transaction
      };
    }
    
    // Handle legacy dashboard transaction type (fallback)
    return {
      _id: transaction._id,
      transactionId: transaction.paymentId || transaction.transactionId || transaction._id.slice(-8).toUpperCase(),
      tournamentName: transaction.tournamentName || 'N/A',
      teamName: transaction.teamName || 'N/A',
      date: transaction.date || transaction.createdAt,
      ageCategory: transaction.ageCategory || 'N/A',
      amount: transaction.amount,
      paymentMode: transaction.paymentMode || transaction.method || 'N/A',
      status: transaction.status || transaction.paidStatus || 'N/A',
      description: transaction.description || `${transaction.milestone} - ${transaction.type}` || 'N/A',
      originalData: transaction
    };
  };

  const formatDate = (dateString: string) => {
    // Handle date format like "01/01/2024"
    if (dateString.includes('/')) {
      return dateString;
    }
    
    // Handle ISO date format
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const badgeStyle = {
      border: '1px solid',
      fontWeight: '500',
      padding: '0.375rem 0.75rem',
      borderRadius: '6px'
    };

    const normalizedStatus = status.toLowerCase();
    
    switch (normalizedStatus) {
      case 'success':
      case 'paid':
        return (
          <Badge 
            bg="success" 
            className="payment-badge"
            style={{
              ...badgeStyle,
              backgroundColor: '#E9FCF2',
              color: '#1B9E5C',
              borderColor: '#1B9E5C'
            }}
          >
            {status === 'paid' ? 'Paid' : 'Success'}
          </Badge>
        );
      case 'failure':
      case 'failed':
        return (
          <Badge 
            bg="danger" 
            className="payment-badge"
            style={{
              ...badgeStyle,
              backgroundColor: '#FFEFF0',
              color: '#FC5C65',
              borderColor: '#FC5C65'
            }}
          >
            Failed
          </Badge>
        );
      case 'pending':
        return (
          <Badge 
            bg="warning" 
            className="payment-badge"
            style={{
              ...badgeStyle,
              backgroundColor: '#FFF8E1',
              color: '#F57C00',
              borderColor: '#F57C00'
            }}
          >
            Pending
          </Badge>
        );
      default:
        return (
          <Badge 
            bg="secondary" 
            className="payment-badge"
            style={badgeStyle}
          >
            {status}
          </Badge>
        );
    }
  };

  const getPaymentMethodBadge = (method: string) => {
    const methodText = method === 'cash' ? 'Cash' :
                      method === 'netbanking' ? 'Net Banking' :
                      method === 'upi' ? 'UPI' :
                      method === 'card' ? 'Card' :
                      method === 'razorpay' ? 'Razorpay' :
                      method;

    return (
      <span className="badge bg-light text-dark">
        {methodText}
      </span>
    );
  };

  const handleDownloadInvoice = async (transaction: UniversalTransaction) => {
    try {
      setIsDownloading(true);
      setDownloadingTransactionId(transaction._id);
      
      await downloadUnifiedInvoice(transaction.originalData);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Failed to download invoice. Please try again.');
    } finally {
      setIsDownloading(false);
      setDownloadingTransactionId(null);
    }
  };

  const handleViewTransaction = (transaction: UniversalTransaction) => {
    if (onViewTransaction) {
      onViewTransaction(transaction.originalData);
    }
  };

  // Transform transactions to universal format
  const universalTransactions = transactions.map(transformToUniversal);
  
  // Apply row limit if specified
  const displayTransactions = maxRows ? universalTransactions.slice(0, maxRows) : universalTransactions;

  // Check if there are any non-failed transactions to show actions column
  const hasNonFailedTransactions = displayTransactions.some(transaction => transaction.status.toLowerCase() !== 'failed');
  const shouldShowActions = showActions && hasNonFailedTransactions;

  // Calculate column count for loading state
  const columnCount = 8 + (shouldShowActions ? 1 : 0);

  if (loading) {
    const LoadingTable = (
      <div className="table-responsive">
        <Table className="table-borderless table-nowrap align-middle mb-0 latest-transactions-table">
          <thead className="table-light">
            <tr>
              <th scope="col" style={{ width: '15%' }}>Transaction ID</th>
              <th scope="col" style={{ width: '20%' }}>Tournament</th>
              <th scope="col" style={{ width: '15%' }}>Team</th>
              <th scope="col" style={{ width: '10%' }}>Date</th>
              <th scope="col" style={{ width: '10%' }}>Age Category</th>
              <th scope="col" style={{ width: '10%' }}>Amount</th>
              <th scope="col" style={{ width: '10%' }}>Payment Mode</th>
              <th scope="col" style={{ width: '10%', minWidth: '100px' }}>Status</th>
              {showActions && <th scope="col" style={{ width: '10%' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, index) => (
              <ShimmerRow 
                key={index} 
                columns={columnCount} 
                height="60px"
              />
            ))}
          </tbody>
        </Table>
      </div>
    );

    return isDashboard ? (
      <Card className="h-100">
        <CardBody>
          {showHeader && (
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">{title}</h5>
              {viewAllLink && (
                <Button 
                  variant="link" 
                  className="text-decoration-none p-0"
                  onClick={() => navigate(viewAllLink)}
                >
                  View all <i className="ph-arrow-right ms-1"></i>
                </Button>
              )}
            </div>
          )}
          {LoadingTable}
        </CardBody>
      </Card>
    ) : (
      <Card>
        <Card.Body className="p-0">
          {LoadingTable}
        </Card.Body>
      </Card>
    );
  }

  const EmptyState = (
    <tr>
      <td colSpan={columnCount} className="text-center py-4">
        <i className="ph-credit-card text-muted" style={{ fontSize: '2rem' }}></i>
        <p className="text-muted mt-2 mb-0">No transactions found</p>
      </td>
    </tr>
  );

  const TableContent = (
    <div className="table-responsive">
      <Table className="table-borderless table-nowrap align-middle mb-0 latest-transactions-table">
        <thead className="table-light">
          <tr>
            <th scope="col" style={{ width: '15%' }}>Transaction ID</th>
            <th scope="col" style={{ width: '20%' }}>Tournament</th>
            <th scope="col" style={{ width: '15%' }}>Team</th>
            <th scope="col" style={{ width: '10%' }}>Date</th>
            <th scope="col" style={{ width: '10%' }}>Age Category</th>
            <th scope="col" style={{ width: '10%' }}>Amount</th>
            <th scope="col" style={{ width: '10%' }}>Payment Mode</th>
            <th scope="col" style={{ width: '10%', minWidth: '100px' }}>Status</th>
            {shouldShowActions && <th scope="col" style={{ width: '10%' }}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {displayTransactions.length > 0 ? (
            displayTransactions.map((transaction) => (
              <tr key={transaction._id}>
                <td>
                  <span className="fw-medium text-primary">
                    {transaction.transactionId}
                  </span>
                </td>
                <td>
                  <span className="text-body-secondary">
                    {transaction.tournamentName}
                  </span>
                </td>
                <td>
                  <span className="text-body-secondary">
                    {transaction.teamName}
                  </span>
                </td>
                <td>
                  <span className="text-body-secondary">
                    {formatDate(transaction.date)}
                  </span>
                </td>
                <td>
                  <span className="text-body-secondary">
                    {transaction.ageCategory}
                  </span>
                </td>
                <td>
                  <span className="fw-medium">
                    {formatAmount(transaction.amount)}
                  </span>
                </td>
                <td>
                  {getPaymentMethodBadge(transaction.paymentMode)}
                </td>
                <td>
                  {getStatusBadge(transaction.status)}
                </td>
                {shouldShowActions && transaction.status.toLowerCase() !== 'failed' && (
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleViewTransaction(transaction)}
                        style={{
                          width: '32px',
                          height: '32px',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="View Details"
                      >
                        <i className="ri-eye-line" style={{ fontSize: '14px' }}></i>
                      </Button>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => handleDownloadInvoice(transaction)}
                        disabled={isDownloading && downloadingTransactionId === transaction._id}
                        style={{
                          width: '32px',
                          height: '32px',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isDownloading && downloadingTransactionId === transaction._id ? '#f8f9fa' : '#ffffff',
                          color: isDownloading && downloadingTransactionId === transaction._id ? '#6c757d' : '#28a745',
                          cursor: isDownloading && downloadingTransactionId === transaction._id ? 'not-allowed' : 'pointer'
                        }}
                        title={isDownloading && downloadingTransactionId === transaction._id ? "Downloading..." : "Download Invoice"}
                      >
                        {isDownloading && downloadingTransactionId === transaction._id ? (
                          <div className="shimmer-loader" style={{
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 1.5s infinite'
                          }}></div>
                        ) : (
                          <i className="ri-download-line" style={{ fontSize: '14px' }}></i>
                        )}
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))
          ) : (
            EmptyState
          )}
        </tbody>
      </Table>
    </div>
  );

  if (isDashboard) {
    return (
      <Card className="h-100">
        <CardBody>
          {showHeader && (
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">{title}</h5>
              {viewAllLink && (
                <Button 
                  variant="link" 
                  className="text-decoration-none p-0"
                  onClick={() => navigate(viewAllLink)}
                >
                  View all <i className="ph-arrow-right ms-1"></i>
                </Button>
              )}
            </div>
          )}
          {TableContent}
          <SharedDownloadOverlay 
            isVisible={isDownloading} 
            message="Generating Invoice..." 
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Body className="p-0">
        {TableContent}
        <SharedDownloadOverlay 
          isVisible={isDownloading} 
          message="Generating Invoice..." 
        />
      </Card.Body>
    </Card>
  );
};

export default TransactionTable; 